import { useLayoutEffect, useRef, useState } from 'react';

import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import { Box } from './components/box';

export const DataGridPaginationMode = {
  Client: 'client',
  Server: 'server'
} as const;

export type DataGridPaginationModeType =
  (typeof DataGridPaginationMode)[keyof typeof DataGridPaginationMode];

type DataGridWrapperProps<T> = {
  rows: T[];
  columns: GridColDef[];
  loading?: boolean;
  onRowClick?: (row: T) => void;
  pageSize?: number;
  paginationMode?: DataGridPaginationModeType;
  page?: number;
  rowCount?: number;
  onPaginationChange?: (page: number, pageSize: number) => void;
  getRowId?: (row: T) => string | number;
};

const ROW_HEIGHT = 52;
const HEADER_HEIGHT = 56;
const PAGINATOR_HEIGHT = 52;
const BORDER_PADDING = 2;
const MIN_TABLE_HEIGHT = 400;
const MAX_TABLE_HEIGHT_PX = 800;
const AUTO_HEIGHT_THRESHOLD = 10;

const WIDTH_UPDATE_THRESHOLD_PX = 50;

export const DataGridWrapper = <T,>({
  rows,
  columns,
  loading = false,
  onRowClick,
  pageSize = 25,
  paginationMode = DataGridPaginationMode.Client,
  page,
  rowCount,
  onPaginationChange,
  getRowId
}: DataGridWrapperProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const resizeTimeoutRef = useRef<number | null>(null);
  const lastWidthRef = useRef<number>(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const updateWidth = () => {
      if (resizeTimeoutRef.current) {
        globalThis.cancelAnimationFrame(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = globalThis.requestAnimationFrame(() => {
        const width = container.getBoundingClientRect().width;
        const delta = Math.abs(width - lastWidthRef.current);
        if (delta >= WIDTH_UPDATE_THRESHOLD_PX || lastWidthRef.current === 0) {
          lastWidthRef.current = width;
          setContainerWidth(width);
        }
      });
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    const initialWidth = container.getBoundingClientRect().width;
    lastWidthRef.current = initialWidth;
    setContainerWidth(initialWidth);

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current) {
        globalThis.cancelAnimationFrame(resizeTimeoutRef.current);
      }
    };
  }, []);

  const responsiveColumns = columns.map(col => {
    const newCol = { ...col };
    if (newCol.flex !== undefined && newCol.flex !== null) {
      delete newCol.width;
    }
    return newCol;
  });

  const handleRowClick = onRowClick
    ? (params: { row: T }) => {
        onRowClick(params.row);
      }
    : undefined;

  const isServerPagination = paginationMode === DataGridPaginationMode.Server;
  const showFooterPagination = true;
  const displayedRowsCount = Math.min(rows.length, pageSize);
  const paginatorHeight = showFooterPagination ? PAGINATOR_HEIGHT : 0;
  const calculatedHeight =
    HEADER_HEIGHT + displayedRowsCount * ROW_HEIGHT + paginatorHeight + BORDER_PADDING;
  const shouldUseAutoHeight = displayedRowsCount <= AUTO_HEIGHT_THRESHOLD;

  const tableHeight = shouldUseAutoHeight
    ? calculatedHeight
    : Math.min(Math.max(calculatedHeight, MIN_TABLE_HEIGHT), MAX_TABLE_HEIGHT_PX);

  return (
    <Box
      ref={containerRef}
      sx={{ width: '100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}
    >
      <DataGrid
        key={containerWidth}
        rows={rows}
        columns={responsiveColumns}
        loading={loading}
        pageSizeOptions={[10, 20, 25, 50, 100]}
        paginationMode={
          isServerPagination ? DataGridPaginationMode.Server : DataGridPaginationMode.Client
        }
        rowCount={isServerPagination ? rowCount : undefined}
        paginationModel={
          isServerPagination && typeof page === 'number' ? { page, pageSize } : undefined
        }
        onPaginationModelChange={
          isServerPagination && onPaginationChange
            ? model => {
                onPaginationChange(model.page, model.pageSize);
              }
            : undefined
        }
        initialState={{
          pagination: {
            paginationModel: {
              pageSize
            }
          }
        }}
        onRowClick={handleRowClick}
        getRowId={getRowId}
        disableRowSelectionOnClick
        autoHeight={shouldUseAutoHeight}
        sx={{
          width: '100%',
          flex: 1,
          minWidth: 0,
          '& .MuiDataGrid-root': {
            width: '100%'
          },
          '& .MuiDataGrid-main': {
            width: '100%'
          },
          ...(shouldUseAutoHeight
            ? {
                maxHeight: 'calc(100vh - 400px)'
              }
            : {
                height: tableHeight,
                maxHeight: 'calc(100vh - 400px)',
                '& .MuiDataGrid-main': {
                  maxHeight: 'calc(100vh - 400px)',
                  width: '100%'
                },
                '& .MuiDataGrid-virtualScroller': {
                  maxHeight: `${Math.min(tableHeight - HEADER_HEIGHT - paginatorHeight, 800 - HEADER_HEIGHT - paginatorHeight)}px !important`
                }
              })
        }}
      />
    </Box>
  );
};
